/**
 * page-loader.js
 *
 * A slim progress bar pinned to the top of the viewport that reports navigation
 * latency — the gap between a customer clicking through to another page and that
 * page painting. It is deliberately NOT a full-screen preloader: the storefront
 * is server-rendered, so an overlay on first paint would hide content the browser
 * has already delivered and push out LCP for no benefit.
 *
 * Behaviour notes:
 *  - Nothing is shown for the first ~150ms. Fast navigations complete inside that
 *    window and never flash the bar, which is what makes it read as polished
 *    rather than busy.
 *  - Progress "trickles" toward a 90% cap while waiting. Real progress is not
 *    observable for a document navigation, so the bar communicates *liveness*,
 *    then snaps to 100% as the page unloads.
 *  - Listeners are attached in the bubble phase and bail on `defaultPrevented`.
 *    That is what keeps the bar off AJAX interactions — cart drawer, quick add,
 *    product forms, predictive search — without hard-coding a list of selectors.
 *    Anything the theme handles in-page has already called preventDefault by the
 *    time the event reaches us.
 */
(function () {
  'use strict';

  // The theme editor reloads sections constantly; a nav bar there is pure noise.
  if (window.Shopify && window.Shopify.designMode) return;

  var SHOW_DELAY = 150; // grace period before the bar appears at all
  var TRICKLE_INTERVAL = 220;
  var CAP = 0.9; // never reach 100% until the navigation actually commits
  var FINISH_DURATION = 300; // time for the fill-to-100% animation to play out
  var SAFETY_TIMEOUT = 15000; // navigation never happened — clean up regardless

  // Clicks that trigger a download leave the page in place, so the bar would
  // otherwise hang at 90% forever. COA and spec-sheet PDFs make this a real case.
  var FILE_EXTENSION = /\.(pdf|zip|csv|xlsx?|docx?|pptx?|png|jpe?g|gif|webp|svg|mp4|mp3|txt)$/i;

  var container = null;
  var bar = null;
  var progress = 0;
  var active = false;
  var visible = false;
  var showTimer = null;
  var trickleTimer = null;
  var finishTimer = null;
  var safetyTimer = null;

  function mount() {
    if (bar) return bar;

    container = document.createElement('div');
    container.className = 'page-loader';
    // Purely a visual affordance. Screen readers announce the new document on
    // navigation, so exposing a second, redundant progress signal is noise.
    container.setAttribute('aria-hidden', 'true');

    bar = document.createElement('div');
    bar.className = 'page-loader__bar';
    container.appendChild(bar);
    document.body.appendChild(container);

    return bar;
  }

  function setProgress(value) {
    progress = value;
    mount().style.transform = 'scaleX(' + value + ')';
  }

  function clearTimers() {
    clearTimeout(showTimer);
    clearTimeout(trickleTimer);
    clearTimeout(finishTimer);
    clearTimeout(safetyTimer);
  }

  function trickle() {
    trickleTimer = setTimeout(function () {
      if (!active) return;
      // Asymptotic approach to the cap: each step covers a fraction of what is
      // left, so the bar decelerates naturally on a slow connection instead of
      // stalling dead at a fixed value.
      var remaining = CAP - progress;
      if (remaining > 0.005) setProgress(progress + remaining * 0.12);
      trickle();
    }, TRICKLE_INTERVAL);
  }

  function start() {
    if (active) return;
    active = true;
    clearTimers();

    showTimer = setTimeout(function () {
      if (!active) return;
      visible = true;
      document.documentElement.classList.add('page-loading');
      setProgress(0.08);
      trickle();
    }, SHOW_DELAY);

    safetyTimer = setTimeout(reset, SAFETY_TIMEOUT);
  }

  function done() {
    if (!active) return;
    active = false;
    clearTimers();

    // Navigation resolved before the grace period elapsed — the bar never
    // painted, so tear down silently rather than flashing a completed bar.
    if (!visible) {
      reset();
      return;
    }

    setProgress(1);
    finishTimer = setTimeout(reset, FINISH_DURATION);
  }

  function reset() {
    clearTimers();
    active = false;
    visible = false;
    document.documentElement.classList.remove('page-loading');

    if (!bar) return;
    // Rewind to empty without animating, then force a reflow so the next
    // navigation animates cleanly from zero instead of inheriting the rewind.
    bar.style.transition = 'none';
    setProgress(0);
    void bar.offsetWidth;
    bar.style.transition = '';
  }

  function navigates(anchor) {
    if (!anchor || !anchor.href) return false;
    if (anchor.hasAttribute('download')) return false;
    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.closest('[data-no-page-loader]')) return false;

    var url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (error) {
      return false;
    }

    // mailto:, tel:, javascript: — no document load follows.
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    // Cross-origin destinations are almost always new-tab links here, and we
    // cannot tell a navigation from a download on another origin.
    if (url.origin !== window.location.origin) return false;
    if (FILE_EXTENSION.test(url.pathname)) return false;
    // Same document, hash only: an in-page jump, not a load.
    if (url.hash && url.pathname === window.location.pathname && url.search === window.location.search) {
      return false;
    }

    return true;
  }

  document.addEventListener('click', function (event) {
    // A theme handler upstream already took ownership of this click.
    if (event.defaultPrevented) return;
    // Middle-click and modified clicks open a new tab; this page stays put.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    if (navigates(target.closest('a[href]'))) start();
  });

  document.addEventListener('submit', function (event) {
    if (event.defaultPrevented) return;

    var form = event.target;
    if (!form || form.hasAttribute('data-no-page-loader')) return;
    if (form.closest('[data-no-page-loader]')) return;

    start();
  });

  // Back/forward navigation. On a bfcache restore the DOM comes back exactly as
  // it left — mid-animation bar included — so it has to be wound down explicitly.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) reset();
  });

  // The document is committing to the new page: fill the bar the rest of the way.
  window.addEventListener('pagehide', done);

  // Covers a navigation the customer cancels, or a download dialog that leaves
  // the page in place — the tab regains focus and the bar should not linger.
  window.addEventListener('blur', function () {
    if (active) finishTimer = setTimeout(reset, 1200);
  });
})();
